import { useCallback, useEffect, useState } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import Autoplay from 'embla-carousel-autoplay'
import type { EmblaOptionsType, EmblaCarouselType } from './types'

interface UseEmblaOptions {
    options: EmblaOptionsType
    autoplay?: boolean | { delay: number }
    onSlideChange?: (index: number) => void
    onEmblaInit?: (api: EmblaCarouselType) => void
    externalIndex?: number
}

export function useEmbla({ options, autoplay, onSlideChange, onEmblaInit, externalIndex }: UseEmblaOptions) {
    const plugins = autoplay
        ? [Autoplay({ delay: typeof autoplay === 'object' ? autoplay.delay : 3000, stopOnInteraction: true })]
        : []

    const [emblaRef, emblaApi] = useEmblaCarousel(options, plugins)
    const [selectedIndex, setSelectedIndex] = useState(0)
    const [canScrollPrev, setCanScrollPrev] = useState(false)
    const [canScrollNext, setCanScrollNext] = useState(false)

    const onSelect = useCallback(
        (api: EmblaCarouselType) => {
            const index = api.selectedScrollSnap()
            setSelectedIndex(index)
            setCanScrollPrev(api.canScrollPrev())
            setCanScrollNext(api.canScrollNext())
            onSlideChange?.(index)
        },
        [onSlideChange],
    )

    useEffect(() => {
        if (!emblaApi) return
        onSelect(emblaApi)
        emblaApi.on('select', onSelect)
        emblaApi.on('reInit', onSelect)
        onEmblaInit?.(emblaApi)
        return () => {
            emblaApi.off('select', onSelect)
            emblaApi.off('reInit', onSelect)
        }
    }, [emblaApi, onSelect, onEmblaInit])

    // Sync external index (e.g. thumbnail strip controlled from gallery)
    useEffect(() => {
        if (emblaApi && externalIndex !== undefined) {
            emblaApi.scrollTo(externalIndex)
        }
    }, [emblaApi, externalIndex])

    const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi])
    const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi])
    const scrollTo = useCallback((index: number) => emblaApi?.scrollTo(index), [emblaApi])

    return { emblaRef, emblaApi, selectedIndex, canScrollPrev, canScrollNext, scrollPrev, scrollNext, scrollTo }
}
